import { isNumber } from 'lodash'

/**
 * Helpers für die Talent-Detailseite.
 *
 * - parseAdditionalData: normalisiert das `additional_requested_data`-JSON des
 *   Spaces in gut renderbare Felder (Text/Dropdown) + Datei-Felder + Berufserfahrung.
 * - parseDocumentMeta: leitet aus einem S3-Key lesbare Metadaten ab
 *   (Name, Upload-Datum aus dem eingebetteten Timestamp, zugeordnete Frage).
 * - listDocuments: alle Dateien als einzelne Dokumente, nach Frage sortiert.
 * - replacementFileName: Dateiname für eine Ersatzdatei derselben Frage.
 */

const prettifyKey = (key = '') =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())

// Wert eines additional-data-Eintrags robust auflösen (mirror der bisherigen Logik)
const resolveValue = (data) => {
  if (!data) return ''

  if (typeof data !== 'object') return String(data)

  // Mehrfachauswahl (Dropdown, multiple)
  if (data.multiple && Array.isArray(data.input)) {
    return data.input
      .filter((item) => item && typeof item === 'object' && item.value)
      .map((item) => item.value)
      .join(', ')
  }

  // Einzelauswahl als Array
  if (Array.isArray(data.input) && data.input.length > 0) {
    const first = data.input[0]
    if (first && typeof first === 'object' && first.value) return first.value
    if (first) return String(first)
    return ''
  }

  // Einzel-Dropdown als Objekt
  if (data.input && typeof data.input === 'object' && data.input.value) {
    return data.input.value
  }

  // Text-Input
  if (data.input) return String(data.input)

  // Alternative value-Property
  if (data.value) return String(data.value)

  return ''
}

const isEmptyValue = (value) =>
  !value || value === 'undefined' || value === 'null'

/**
 * @returns {{ workExperience: number|null, fields: Array, fileFields: Array }}
 * fields: { id, label, type, value, multiple }
 * fileFields: { id, label, raw }   (Datei-Anfragen – die echten Dateien kommen über fileControl)
 */
export const parseAdditionalData = (space) => {
  let parsed = {}
  try {
    parsed = JSON.parse(space?.additional_requested_data || '{}') || {}
  } catch (err) {
    parsed = {}
  }

  const { work_experience } = parsed
  const fields = []
  const fileFields = []

  Object.keys(parsed)
    .filter((key) => key !== 'work_experience')
    .forEach((key) => {
      const data = parsed[key]
      if (!data) return

      const label = (
        typeof data === 'object' && data.label ? data.label : prettifyKey(key)
      )
        .toString()
        .replace(/_/g, ' ')

      const type = (typeof data === 'object' && data.type) || 'text'

      if (type === 'file') {
        fileFields.push({ id: key, label, raw: data })
        return
      }

      let value = ''
      try {
        value = resolveValue(data)
      } catch (err) {
        value = ''
      }

      if (isEmptyValue(value)) return

      fields.push({
        id: key,
        label,
        type,
        value,
        multiple: !!(typeof data === 'object' && data.multiple)
      })
    })

  return {
    workExperience: isNumber(work_experience) ? work_experience : null,
    fields,
    fileFields
  }
}

const UUID_START = /^[0-9a-f]{8}-?[0-9a-f]{4}/i

/**
 * Leitet lesbare Metadaten aus einem S3-Datei-Key ab.
 * Keys folgen dem Muster `{fragelabel}-{13-stelliger-timestamp}-{name-oder-uuid}.{ext}`.
 *
 * @param {string} fileKey            – der S3-Key (z. B. "space/.../PDF_Testing-1784099673038-911a3762-...pdf")
 * @param {Array}  fileFields         – aus parseAdditionalData, zum Zuordnen der Frage
 * @returns {{ filename, displayName, extension, uploadedAt: Date|null, questionLabel: string, groupKey: string }}
 */
export const parseDocumentMeta = (fileKey, fileFields = []) => {
  const filename =
    String(fileKey || '')
      .split('/')
      .pop() || ''

  const extMatch = filename.match(/\.([a-zA-Z0-9]{1,5})$/)
  const extension = extMatch ? extMatch[1].toLowerCase() : ''

  const tsMatch = filename.match(/(\d{13})/)
  const uploadedAt =
    tsMatch && !Number.isNaN(Number(tsMatch[1]))
      ? new Date(Number(tsMatch[1]))
      : null

  let questionLabel = ''
  let groupKey = ''
  let displayName = filename.replace(/\.[a-zA-Z0-9]{1,5}$/, '')

  if (tsMatch) {
    const idx = filename.indexOf(tsMatch[1])
    const rawLabel = filename.slice(0, idx).replace(/[-_]+$/, '')
    // Exaktes Label-Segment als Gruppierungsschlüssel: unterscheidet auch Fragen,
    // deren Label ein Präfix einer anderen ist (z.B. "Arbeitszeugnis" vs.
    // "Arbeitszeugnis_2") — der Fuzzy-Match unten ist nur für die Anzeige.
    groupKey = rawLabel.toLowerCase()
    questionLabel = rawLabel.replace(/_/g, ' ').trim()

    const rest = filename
      .slice(idx + tsMatch[1].length)
      .replace(/^[-_]+/, '')
      .replace(/\.[a-zA-Z0-9]{1,5}$/, '')

    // Rest ist eine UUID oder leer -> Frage-Label als Name nutzen; sonst den echten Dateinamen
    if (!rest || UUID_START.test(rest)) {
      displayName = questionLabel || displayName
    } else {
      displayName = rest.replace(/[-_]/g, ' ').trim()
    }
  }

  // Datei einer Datei-Anfrage zuordnen (Prefix-Match auf das Label)
  const norm = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '')
  const matched = fileFields.find((f) => {
    const lbl = norm(f.label)
    const q = norm(questionLabel)
    return lbl && q && (q.startsWith(lbl) || lbl.startsWith(q))
  })

  return {
    filename,
    displayName: displayName || filename,
    extension,
    uploadedAt,
    questionLabel: matched?.label || questionLabel || '',
    groupKey
  }
}

/**
 * Alle Dateien als einzelne Dokumente, sortiert nach Reihenfolge der Fragen,
 * innerhalb einer Frage neueste zuerst.
 *
 * Früher wurden Dateien derselben Frage als Versionen eines Dokuments gruppiert,
 * sichtbar war nur die neueste. Bei Fragen mit mehreren Dateien (Lebenslauf,
 * Anschreiben, Zeugnisse in einem Upload-Feld) lag damit alles außer der
 * zuletzt hochgeladenen Datei eingeklappt und fehlte im PDF-Bundle. Das
 * `multiple`-Flag der Frage taugt nicht zur Unterscheidung, die PBC-App hängt
 * auch ohne es weitere Dateien an. Ersetzen ist jetzt eine eigene Aktion
 * (fileControl deleteFile mit replaced_by) und steht im Verlauf.
 *
 * @param {Array} files      – Dateien aus fileControl/listFilesForSpace ({ key, ... })
 * @param {Array} fileFields – aus parseAdditionalData, für parseDocumentMeta
 * @returns {Array<{ key: string, file: Object, meta: Object }>}
 */
export const listDocuments = (files = [], fileFields = []) => {
  const fieldOrder = (meta) => {
    const index = fileFields.findIndex((f) => f.label === meta.questionLabel)
    return index === -1 ? fileFields.length : index
  }

  return files
    .map((file) => ({
      key: file.key,
      file,
      meta: parseDocumentMeta(file.key, fileFields)
    }))
    .sort(
      (a, b) =>
        fieldOrder(a.meta) - fieldOrder(b.meta) ||
        (b.meta.uploadedAt?.getTime() || 0) -
          (a.meta.uploadedAt?.getTime() || 0)
    )
}

/**
 * Dateiname für eine Datei, die `fileKey` ersetzt: gleiches Label-Segment (damit
 * sie derselben Frage zugeordnet bleibt), neuer Timestamp, Originalname der
 * gewählten Datei. Gleiches Schema wie PBC-App und universal-app.
 */
export const replacementFileName = (fileKey, newName, now = Date.now()) => {
  const filename =
    String(fileKey || '')
      .split('/')
      .pop() || ''
  const tsMatch = filename.match(/(\d{13})/)
  const segment = tsMatch
    ? filename.slice(0, filename.indexOf(tsMatch[1])).replace(/[-_]+$/, '')
    : ''

  return `${segment ? `${segment}-` : ''}${now}-${newName}`
}
